/**
 * The activity panel's remembered state: is it open, and which section was last looked at
 * (design §1.2 — "Collapsed/expanded state and last-opened section should persist per user").
 *
 * ## Why a new key rather than the rail's
 *
 * `DetailPageLayout` already persists a *width* under `dop:detail-rail-width`, shared by both detail
 * pages and clamped to 280–560. That key holds a number in an unrelated range for an unrelated
 * control; reusing it would make a stored width and a stored open-flag fight over one slot. Ours is
 * namespaced `dop:activity-panel:*` so a future panel preference (a width of its own, a default
 * filter pill) appends instead of colliding.
 *
 * ## Why the preference is GLOBAL, not per entity type
 *
 * The question a user is answering by clicking the rail is "do I want the activity panel open" —
 * about themselves, not about picklists. Storing it per entity type means opening the panel on a
 * picklist, navigating to a receiving document, and finding it shut, with no way to tell that the
 * app remembered anything at all. One preference, one behaviour, everywhere. The same argument
 * carries the section: whoever was last reading Activity wants Activity on the next record too.
 *
 * ## Storage may not exist and may throw
 *
 * Vitest runs in the `node` environment (vite.config.ts sets none, and there is no jsdom), so
 * `localStorage` is simply absent under test; hardened profiles and private windows THROW on access
 * rather than returning null. Same guard shape as `tokenService.ts:34-39`. Every path here degrades
 * to {@link DEFAULT_PANEL_PREFS} in silence — a chrome preference is never worth an error boundary.
 */

/** One JSON blob, so open-ness and section can never be read half-updated. */
export const PANEL_PREFS_KEY = 'dop:activity-panel:prefs';

export const PANEL_SECTIONS = ['activity', 'comments'] as const;

export type PanelSection = (typeof PANEL_SECTIONS)[number];

export interface PanelPrefs {
  open: boolean;
  /** Where the panel reopens. Kept even while closed — that is what "last-opened" means. */
  section: PanelSection;
}

/**
 * Closed on a first visit. The rail is the permanent trace of the feature (design §1.1); the panel
 * costs 364px of a detail page, and taking that from someone who has never asked for it is a worse
 * first impression than an icon they have to find once.
 */
export const DEFAULT_PANEL_PREFS: PanelPrefs = { open: false, section: 'activity' };

function storage(): Storage | null {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
    ? window.localStorage
    : null;
}

export function isPanelSection(value: unknown): value is PanelSection {
  return typeof value === 'string' && (PANEL_SECTIONS as readonly string[]).includes(value);
}

/**
 * Coerce anything that came out of storage into a usable preference. Pure, and exported, because it
 * is the only part of this module a test can reach without a DOM.
 *
 * A section that is no longer valid falls back to `activity` rather than throwing. That is not
 * hypothetical: `comments` ships as a coming-soon placeholder, and if it is ever renamed or pulled,
 * every user who last clicked it would otherwise open the panel onto a section that does not exist.
 */
export function normalizePanelPrefs(raw: unknown): PanelPrefs {
  if (typeof raw !== 'object' || raw === null) return DEFAULT_PANEL_PREFS;

  const candidate = raw as Partial<Record<keyof PanelPrefs, unknown>>;

  return {
    open: candidate.open === true,
    section: isPanelSection(candidate.section) ? candidate.section : DEFAULT_PANEL_PREFS.section,
  };
}

export function readPanelPrefs(): PanelPrefs {
  try {
    const raw = storage()?.getItem(PANEL_PREFS_KEY);
    if (!raw) return DEFAULT_PANEL_PREFS;
    return normalizePanelPrefs(JSON.parse(raw));
  } catch {
    // Absent storage, a throwing profile, or a half-written blob from an older shape. All the same
    // answer: the defaults, silently.
    return DEFAULT_PANEL_PREFS;
  }
}

/** Returns what it wrote, so callers can set state from one expression even if the write was a no-op. */
export function writePanelPrefs(prefs: PanelPrefs): PanelPrefs {
  const normalized = normalizePanelPrefs(prefs);
  try {
    storage()?.setItem(PANEL_PREFS_KEY, JSON.stringify(normalized));
  } catch {
    /* QuotaExceededError, or no storage at all — the panel still works, it just forgets. */
  }
  return normalized;
}

/**
 * The rail's click rule (design §1.2): "click an inactive item → open the panel on that section;
 * click the **active** item → collapse".
 *
 * Collapsing KEEPS the clicked section as `section`. It is the last-opened one by definition, and
 * resetting it to `activity` on close would quietly undo a user's choice every time they collapsed
 * the panel — the one action that is supposed to be non-destructive.
 */
export function applyRailClick(current: PanelPrefs, clicked: PanelSection): PanelPrefs {
  const isActive = current.open && current.section === clicked;
  return { open: !isActive, section: clicked };
}

/** `Esc` collapses, `A` toggles Activity — the keyboard half of the same rule. */
export function applyCollapse(current: PanelPrefs): PanelPrefs {
  return { ...current, open: false };
}
